import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
} from "../../components/micro";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import Breadcrumb from "../../components/common/Breadcrumb";
import { useDispatch } from "react-redux";
import {
  createAffirmation,
  editAffirmation,
} from "../../redux/affirmation/slice";

const AddEditAffirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const affirmationData = location?.state?.affirmation;
  const isEditMode = location?.state?.mode;
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const initialValues = useMemo(
    () => ({
      name: affirmationData?.name || "",
    }),
    [affirmationData]
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().nullable().required("Please enter affirmation"),
  });

  const handleConfirm = () => {
    setIsLoading(true);
    const payload = {
      name: formValues?.name,
    };

    const action = affirmationData?._id
      ? editAffirmation({ id: affirmationData._id, ...payload })
      : createAffirmation(payload);

    dispatch(action)
      .then((res) => {
        if (
          res.type === "createAffirmation/fulfilled" ||
          res.type === "editAffirmation/fulfilled"
        ) {
          navigate("/affirmations");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowConfirmationModal(false);
      });
  };

  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => navigate("/affirmations");

  return (
    <>
      <div className="mb-5">
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          {isEditMode === "edit" ? "Edit Affirmation" : "Create Affirmation"}
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={
            isEditMode === "edit" ? "Edit Affirmation" : "Create Affirmation"
          }
          breadCrumbParent3={"Affirmation"}
          breadCrumbTitleKey={"/"}
        />
      </div>

      <div className="w-full bg-bgWhite shadow rounded-lg py-6 px-6">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setFormValues(values);
            setShowConfirmationModal(true);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1">
                <InputWithLabel
                  idFromName="name"
                  label="Affirmation*"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  errors={errors.name && touched.name}
                  className={`${
                    errors.name && touched.name
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  } w-full `}
                  errorsText={errors.name}
                  disabled={isEditMode === "edit" && !isEditing}
                />
              </div>
              <div className="flex justify-end gap-3 mt-3">
                <DefaultPrimaryButton
                  type="button"
                  value={isEditMode === "edit" ? "Back" : "Cancel"}
                  onClick={handleCancelClick}
                />
                {isEditMode === "edit" ? (
                  isEditing ? (
                    <>
                      <DefaultButton
                        type="submit"
                        value="Save"
                        onClick={(e) => {}}
                      />
                    </>
                  ) : (
                    <DefaultButton
                      value="Edit"
                      onClick={handleEditClick}
                      type="submit"
                    />
                  )
                ) : (
                  <>
                    <DefaultButton type="submit" value="confirm" />
                  </>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {showConfirmationModal && (
        <ConfirmationModel
          confirmText={isEditMode === "edit" ? "Edit" : "Create"}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirm}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default AddEditAffirmation;
